import { ArrowBack, Home } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Breadcrumbs, CardContent, Chip, IconButton, Skeleton, Stack, Tab } from "@mui/material";
import BreadcrumbLink from "components/breadcrumbs/BreadcrumbLink";
import IndieCard from "components/IndieCard";
import { H3 } from "components/Typography";
import { CommunityContext, CommunityContextProvider } from "contexts/communities/CommunityContext";
import { TariffsContextProvider } from "contexts/tariffs/TariffsContext";
import useAuth from "hooks/useAuth";
import { lazy, Suspense, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import CommunityDetailsTab from "./detail/CommunityDetailsTab";

const LazyCommunityClientsTab = lazy(() => import("./detail/CommunityClientsTabs"));
const LazyCommunityEnergyTab = lazy(() => import("./detail/CommunityEnergyTab"));
const LazyCommunityInstallationMembersTab = lazy(() => import("./detail/CommunityInstallationMembersTab"));
const LazyCommunityContractsTab = lazy(() => import("./detail/CommunityContractsTab"));

const CommunityDetail = (props) => {

    const { community_id: id } = useParams();

    return (
        <CommunityContextProvider id={id}>
            <TariffsContextProvider>
                <CommunityDetailContent {...props} />
            </TariffsContextProvider>
        </CommunityContextProvider>
    );
};

export const CommunityStatusChip = ({ status }) => {
    const { t } = useTranslation();
    const color = status === "active" ? "primary" : "disabled";

    return (
        <Chip label={t(`label.community.status.${status}`)} sx={{ minWidth: 100 }} color={color} />
    )
}

const CommunityDetailContent = () => {

    const { t } = useTranslation();
    const { community, submitting } = useContext(CommunityContext);
    const [currentTab, setCurrentTab] = useState(null);
    const navigate = useNavigate();
    const { hasPermissionGroup } = useAuth();
    const location = useLocation();

    const handleChange = (e, newValue) => {
        setCurrentTab(newValue);
        navigate(newValue, { replace: true });
    };

    useEffect(() => {
        let tab = window.location.pathname.split("/").pop();
        setCurrentTab(tab || "details");
    }, []);

    const backToUrl = () => location?.state?.backUrl ? navigate(location?.state?.backUrl) : navigate("/communities");

    // submitting is shared CommunityContext state, also toggled by unrelated actions triggered
    // deep inside a tab's own dialogs (e.g. creating an installation) — only treat it as "loading
    // the page" before the community has ever loaded, otherwise every such action would unmount
    // this whole subtree (and any in-flight dialog/confirmation state) mid-flight.
    if (submitting && !community) {
        return <Skeleton variant="rectangular" height={400} />;
    }

    if (!community) {
        return null;
    }

    return (
        <Stack spacing={2}>
            <IndieCard>
                <CardContent>
                    <Stack direction="row" spacing={2}>
                        <IconButton onClick={backToUrl} size="large" color="primary" aria-label="back">
                            <ArrowBack />
                        </IconButton>
                        <Stack direction="column" spacing={1}>
                            <Breadcrumbs>
                                <BreadcrumbLink value={"label.menu.home"} href="/" icon={<Home sx={{ mr: 0.5 }} fontSize="inherit" />} />
                                <BreadcrumbLink value={"label.menu.communities"} href="/communities" />
                                <BreadcrumbLink value={"label.menu.viewCommunity"} />
                            </Breadcrumbs>
                            <H3>{community.name}</H3>
                        </Stack>
                        {hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && (
                            <Box sx={{ flexGrow: 1, display: "inline-flex", justifyContent: "flex-end" }}>
                                <CommunityStatusChip status={community.status} />
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </IndieCard>
            <IndieCard>
                <CardContent>
                    <TabContext value={currentTab}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <TabList onChange={handleChange} >
                                <Tab data-testid="tab_details" label={t("label.menu.details")} index={0} value="details" />
                                <Tab data-testid="tab_energy" label={t("label.menu.energy")} index={1} value="energy" />
                                {(hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && community.managing_entity) && <Tab data-testid="tab_members" label={t("label.menu.installationMembers")} index={2} value="members" />}
                                <Tab data-testid="tab_clients" label={t("label.menu.clients")} index={2} value="clients" />
                                <Tab data-testid="tab_contracts" label={t("btn.physicalUnit.tariffPlans")} index={3} value="contracts" />
                            </TabList>
                        </Box>
                        <TabPanel value={currentTab} sx={{ pt: 2 }}>
                            <Suspense fallback={<Skeleton variant="rectangular" height={400} />}>
                            <Routes>
                                <Route index element={<CommunityDetailsTab />} />
                                <Route path={`details`} element={<CommunityDetailsTab />} />
                                <Route path={`energy`} element={<LazyCommunityEnergyTab />} />
                                {(hasPermissionGroup("ENERGY_AGENT_EGAC_USER") && community.managing_entity) && <Route path={`members`} element={<LazyCommunityInstallationMembersTab />} />}
                                <Route path={`contracts`} element={<LazyCommunityContractsTab />} />
                                <Route path={`clients`} element={<LazyCommunityClientsTab />} />
                            </Routes>
                            </Suspense>
                        </TabPanel>
                    </TabContext>
                </CardContent>
            </IndieCard>
        </Stack>
    )
};

export default CommunityDetail;